import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  Tooltip,
  useColorModeValue as mode,
  useDisclosure,
} from '@chakra-ui/react';
import { ColumnDef, Table } from '@tanstack/react-table';
import { synchronizationAction } from 'actions';
import AmatriumSkeleton from 'components/AmatriumSkeleton';
import Empty from 'components/Empty';
import ConfirmModal from 'components/Modal/ConfirmModal';
import ReactTable from 'components/ReactTable';
import StateText from 'components/StateText';
import { dateFormat } from 'helpers/date';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegFile, FaRegFolderOpen, FaSync } from 'react-icons/fa';
import { IoMdReturnLeft, IoMdSkipBackward } from 'react-icons/io';
import { Link, useHistory } from 'react-router-dom';

import IndeterminateCheckbox from './IndeterminateCheckbox';

interface Document {
  id: string;
  state: string;
  sync_by: string;
  is_folder: boolean;
  name: string;
  display_name: string;
  path: string;
  size: number;
  last_modified: string;
  last_modified_by: Record<string, any>;
}

const getProjectName = (url: string) => url.slice(url.lastIndexOf('/') + 1);

const DocumentList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    isOpen: isOpenSyncModal,
    onOpen: onOpenSyncModal,
    onClose: onCloseSyncModal,
  } = useDisclosure();
  const { isOpen: isOpenFolder, onOpen: onOpenFolder, onClose: onCloseFolder } = useDisclosure();
  const { documents, current, isLoading } = useAppSelector((state) => state.synchronization);
  const [tempSelectFolder, setTempSelectFolder] = useState({
    folder_name: '',
    folder_path: '',
  });

  const columns = useMemo<ColumnDef<Document>[]>(
    () => [
      {
        id: 'index',
        size: 50,
        header: '',
        cell: ({ row }) => {
          return (
            <div className=''>
              {row.original.is_folder ? (
                <IndeterminateCheckbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler(),
                    onClick: (e) => {
                      onSelectRow(e, row.original);
                    },
                  }}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'display_name',
        header: t('syncProject.documentName'),
        size: 500,
        cell: ({ row, table, cell }) => {
          return (
            <HStack>
              {row.original.is_folder ? <FaRegFolderOpen /> : <FaRegFile />}
              <Text
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  _hover: {
                    textDecoration: row.original.is_folder ? 'underline' : 'none',
                  },
                }}
                onClick={(e) => handleOpenFolder(e, row.original, table)}
              >
                {cell.getValue() as string}
              </Text>
            </HStack>
          );
        },
      },
      {
        accessorKey: 'sync_by',
        size: 300,
        header: t('syncProject.syncBy'),
        cell: (info) => (
          <Text
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {info.getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: 'state',
        header: t('syncProject.state'),
        cell: (info) => {
          return (
            <Text
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {info.getValue() ? <StateText statusKey={info.getValue() as string} /> : ''}
            </Text>
          );
        },
      },
      {
        accessorKey: 'last_modified',
        header: t('syncProject.lastModified'),
        cell: (info) => {
          return (
            <>{info.getValue() ? <Text>{dateFormat(info.getValue() as string)}</Text> : null}</>
          );
        },
      },
      {
        accessorKey: 'size',
        header: t('syncProject.size_(KB)'),
        cell: (info) => {
          return (
            <>{info.getValue() ? <Text>{(info.getValue() as number).toFixed(2)}</Text> : null}</>
          );
        },
      },
    ],
    []
  );

  const handleOpenFolder = (
    e: React.MouseEvent<HTMLParagraphElement, MouseEvent>,
    row: Document,
    table: Table<Document>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!row.is_folder) return;

    if (table.getSelectedRowModel().flatRows.length) {
      setTempSelectFolder({
        folder_name: row.display_name,
        folder_path: row.path,
      });
      return onOpenFolder();
    } else {
      dispatch(
        synchronizationAction.updateCurrentFolder({
          folder_name: row.display_name,
          folder_path: row.path,
        })
      );

      dispatch(synchronizationAction.fetchDocumentListByProject(row.path));
    }
  };

  const resetProjectSelection = () => {
    dispatch(synchronizationAction.resetProject());
  };

  const fetchDocumentList = () => {
    dispatch(
      synchronizationAction.updateCurrentFolder({
        folder_name: tempSelectFolder.folder_name,
        folder_path: tempSelectFolder.folder_path,
      })
    );
    if (tempSelectFolder.folder_path) {
      dispatch(synchronizationAction.fetchDocumentListByProject(tempSelectFolder.folder_path));
    }
  };

  const onSyncDocument = async () => {
    dispatch(synchronizationAction.syncDocumentProcessing(true));
    dispatch(synchronizationAction.syncDocumentRequest());
  };

  const onSelectRow = (e: React.MouseEvent<HTMLInputElement, MouseEvent>, row: Document) => {
    e.stopPropagation();
    dispatch(synchronizationAction.updateCurrentSelectedFolders(row));
  };

  const getCurrentFolderName = (folderPath: string) => {
    const parts = folderPath.split('/').filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : '';
  };

  const handleBackBtn = () => {
    let getParentFolderPath = current.folder_path.slice(0, current.folder_path.lastIndexOf('/'));
    let getParentFolderName = getCurrentFolderName(getParentFolderPath);

    if (getParentFolderPath === '') {
      getParentFolderName = '';
      getParentFolderPath = '';
    }

    dispatch(
      synchronizationAction.updateCurrentFolder({
        folder_name: getParentFolderName,
        folder_path: getParentFolderPath,
      })
    );
    dispatch(synchronizationAction.fetchDocumentListByProject(getParentFolderPath));
  };

  useEffect(() => {
    if (!current.connection_id) {
      history.push('/synchronization');
    }
  }, []);

  return (
    <>
      <Stack>
        <Flex
          mb={{
            base: '0.8rem',
            md: '1rem',
            xl: '1.25rem',
            '2xl': '1.25rem',
          }}
        >
          <Tooltip
            label={`${getProjectName(current?.site_url || '') + current.folder_path}`}
            placement='bottom-start'
          >
            <Heading
              as='h3'
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                paddingInline: 4,
              }}
              mr='1rem'
              pt='50px'
              fontWeight={'bold'}
              size='md'
            >
              {`${getProjectName(current?.site_url || '') + current.folder_path}`}
            </Heading>
          </Tooltip>
        </Flex>

        <HStack justifyContent={'flex-end'} spacing={3}>
          {current.folder_path === '' ? (
            <></>
          ) : (
            <Button
              sx={{
                color: 'gray.500',
                fontWeight: 'bold',
              }}
              leftIcon={<IoMdSkipBackward />}
              onClick={handleBackBtn}
              variant={'amatrium_back_btn'}
            >
              {t('common.back')}
            </Button>
          )}

          <Link to={'/sync-projects'}>
            <Button
              sx={{
                color: 'gray.500',
                fontWeight: 'bold',
              }}
              leftIcon={<IoMdReturnLeft />}
              onClick={resetProjectSelection}
              variant={'amatrium_view_btn'}
            >
              {t('syncProject.projectList')}
            </Button>
          </Link>
          <Button
            sx={{
              color: 'gray.500',
              fontWeight: 'bold',
            }}
            isDisabled={isLoading}
            leftIcon={<FaSync />}
            onClick={onOpenSyncModal}
            variant='amatrium_submit'
          >
            {t('syncProject.startToSync')}
          </Button>
        </HStack>

        <Box border={mode('0.5px solid rgb(209, 213, 219)', '')}>
          {isLoading ? (
            <AmatriumSkeleton options={null} />
          ) : (
            <>
              {documents?.list?.length ? (
                <ReactTable columns={columns} data={documents.list} enableRowSelection />
              ) : (
                <HStack justifyContent={'center'}>
                  <Empty content={''} />
                </HStack>
              )}
            </>
          )}
        </Box>
      </Stack>

      <ConfirmModal
        isOpen={isOpenSyncModal}
        onClose={onCloseSyncModal}
        action={onSyncDocument}
        dataHeader={t('syncProject.confirmStartNewSync')}
        dataBody={t('syncProject.confirmStartNewSyncContent')}
        btnAction={t('common.sync')}
        color={'red'}
      />

      <ConfirmModal
        isOpen={isOpenFolder}
        onClose={onCloseFolder}
        action={fetchDocumentList}
        dataHeader={t('common.confirmToContinue')}
        dataBody={t('syncProject.confirmSelectedBeRemoved')}
        btnAction={t('common.continue')}
        color={'red'}
      />
    </>
  );
};

export default DocumentList;
